import { Chart } from "chart.js/auto";
import "chartjs-adapter-date-fns";
import { displayToast } from "../modules/utils";
import { ar, de, enUS, es, fr, nl, tr } from "date-fns/locale";

export default class LineChart {
  constructor(node, data) {
    this.element = node;
    this.chart = this.element.querySelector(".js-canvas-chart");
    this.tooltipEl = this.element.querySelector(".js-chart-tooltip");
    this.tooltipInner = this.element.querySelector(".js-tooltip-inner");
    this.triggerElements = this.element.querySelectorAll(".js-chart-trigger");

    this.canvasContext = this.element.querySelector("canvas").getContext("2d");

    this.image = new Image();
    this.gradient = this.canvasContext.createLinearGradient(0, 0, 0, 400);

    this.determineColors(); // we do this to determine the colors for the chart since we have a contrast mode

    this.gradient.addColorStop(0, this.gradientBaseColor);
    this.gradient.addColorStop(0.8, "rgba(255,255,255,0)");
    this.gradient.addColorStop(1, "rgba(255,255,255,0)");

    this.locale = this.getLocale();
    this.data = data;

    this.updateData(this.getActiveTrigger().dataset.trigger);

    this.triggerElements.forEach((triggerElement) => {
      triggerElement.addEventListener("click", () => {
        this.handleTriggerClick(triggerElement);
      });
    });

    window.addEventListener("contrast-toggled", () => {
      this.determineColors();

      if (this.chartObject) {
        this.chartObject.destroy();
        this.updateData(this.getActiveTrigger().dataset.trigger);
      }
    });
  }

  determineColors() {
    if (document.documentElement.dataset.bsTheme === "contrast") {
      this.borderColor = "#953e04";
      this.gradientBaseColor = "rgba(149, 62, 4, 0.50)";
      this.image.src = "/assets/images/chart_pointer_contrast.svg";
    } else {
      this.borderColor = "#eb6209";
      this.gradientBaseColor = "rgba(235, 98, 9, 0.50)";
      this.image.src = "/assets/images/chart_pointer.svg";
    }
  }

  getActiveTrigger() {
    return this.element.querySelector(".js-chart-trigger.active");
  }

  handleTriggerClick(triggerElement) {
    const activeTrigger = this.getActiveTrigger();
    activeTrigger.classList.remove("active");
    triggerElement.classList.add("active");

    if (this.chartObject) {
      this.chartObject.destroy();
      this.updateData(triggerElement.dataset.trigger);
    }
  }

  getLocale() {
    // TODO: might have to add more language support
    switch (document.documentElement.lang.toLowerCase()) {
      case "nl":
        return nl;
      case "es":
        return es;
      case "de":
        return de;
      case "fr":
        return fr;
      case "ar":
        return ar;
      case "tr":
        return tr;
      default:
        return enUS;
    }
  }

  updateData(trigger) {
    let chartData = [];

    switch (trigger) {
      case "earnings":
        chartData = this.data.earnings;
        break;
      case "pgv":
        chartData = this.data.pgv;
        break;
      case "pv":
        chartData = this.data.pv;
        break;
      default:
        console.error("Could not find trigger", trigger);
        displayToast(this.element.dataset.errorMessage);
        break;
    }

    this.initialize(chartData);
  }

  initialize(chartData) {
    chartData = chartData.reverse();
    this.chartObject = new Chart(this.chart, {
      type: "line",
      data: {
        datasets: [
          {
            backgroundColor: this.gradient,
            data: chartData,
            fill: true,
            tension: 0,
            cubicInterpolationMode: "linear",
            borderJoinStyle: "round",
            borderColor: this.borderColor,
            borderWidth: 1.5,
            pointStyle: this.image,
            pointRadius: 9,
          },
        ],
      },
      options: {
        scales: {
          y: {
            min: 0,
            border: {
              dash: [3, 3],
              width: 0,
            },
            grid: {
              drawTicks: false,
            },
            ticks: {
              color: "#4C4C4E",
              font: { family: "Mukta", size: 16, weight: "normal" },
              padding: 16,
            },
          },
          x: {
            type: "time",
            grid: {
              display: false,
            },
            border: { display: false },
            time: {
              parser: "yyyy-MM",
              unit: "month",
              tooltipFormat: "MMMM yyyy",
              displayFormats: {
                month: "MMM yyyy",
              },
            },
            ticks: {
              color: "#4C4C4E",
              font: { family: "Mukta", size: 16, weight: "normal" },
              padding: 16,
            },
            adapters: {
              date: {
                locale: this.locale,
              },
            },
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        onHover: (event, chartElement) => {
          event.native.target.style.cursor = chartElement[0] ? "pointer" : "default";
        },
        plugins: {
          tooltip: {
            intersect: true,
            position: "nearest",
            mode: "index",
            backgroundColor: "#1f1f1f",
            titleMarginBottom: 0,
            titleFont: { family: "Mukta", size: 16, weight: "bold" },
            bodyFont: { family: "Mukta", size: 16, weight: "normal" },
            caretSize: 12,
            cornerRadius: 8,
            padding: { x: 16, y: 12 },
            displayColors: false,
            bodySpacing: 0,
            animation: {
              duration: 0.15,
              easing: "easeInOutCirc",
            },
            callbacks: {
              afterTitle: (context) => {
                context.forEach((contxt) => {
                  contxt.formattedValue = contxt.formattedValue;
                });
              },
            },
          },
          legend: {
            display: false,
          },
        },
      },
    });
  }
}
